import React from 'react';
import {
  CalendarRange,
  Check,
  ClipboardList,
  Clock,
  Briefcase,
  Building,
} from 'lucide-react';
import { ScrollReveal, StaggerContainer, StaggerItem } from '@/components/ui/ScrollReveal';
import { Container, Section } from '@/components/layout/Container';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

const engagementTiers = [
  {
    id: 'advisory',
    name: 'Advisory',
    description: 'Strategic consultation on specific topics or projects',
    price: '3,000',
    currency: 'NOK',
    unit: '/hour',
    icon: <Clock className="w-5 h-5" />,
    bestFor: 'Focused reviews, decision support, and rapid strategic validation.',
    timeline: '1-2 week cycles',
    deliverables: [
      'Risk memo with prioritised actions',
      'Executive summary for decision meeting',
      'Recommended next sprint scope',
    ],
    features: [
      'Phone/video consultations',
      'Written recommendations',
      'Risk assessment',
      'Strategic guidance',
      '48-hour response time',
    ],
    cta: 'Schedule Consultation',
    href: '/contact?intent=schedule-consultation&tier=advisory',
    highlighted: false,
  },
  {
    id: 'project',
    name: 'Project-Based',
    description: 'Comprehensive engagement for specific initiatives',
    price: '140,000',
    currency: 'NOK',
    unit: '/week',
    icon: <Briefcase className="w-5 h-5" />,
    bestFor: 'Execution-heavy initiatives requiring clinical, product, and operations alignment.',
    timeline: '6-12 week mandates',
    deliverables: [
      'Program blueprint with governance checkpoints',
      'Clinical-technical decision framework',
      'Implementation and escalation roadmap',
    ],
    features: [
      'Dedicated advisory hours',
      'On-site availability',
      'Technical architecture review',
      'Stakeholder presentations',
      'Implementation roadmap',
      'Priority scheduling',
    ],
    cta: 'Discuss Project',
    href: '/contact?intent=schedule-consultation&tier=project',
    highlighted: true,
  },
  {
    id: 'executive',
    name: 'Executive / Board',
    description: 'Ongoing strategic partnership at leadership level',
    price: 'Custom',
    currency: '',
    unit: 'Structured',
    icon: <Building className="w-5 h-5" />,
    bestFor: 'Ongoing strategic guidance where governance and speed must co-exist.',
    timeline: 'Quarterly or annual retainers',
    deliverables: [
      'Board-ready strategic briefing package',
      'Risk governance review cadence',
      'Leadership alignment facilitation',
    ],
    features: [
      'Board-level advisory',
      'Monthly retainers available',
      'Strategic planning sessions',
      'Risk governance oversight',
      'C-suite collaboration',
      '24/7 availability for urgent matters',
    ],
    cta: 'Request Proposal',
    href: '/contact?intent=schedule-consultation&tier=executive',
    highlighted: false,
  },
];

export const Engagement: React.FC = () => {
  return (
    <Section id="engagement" dark>
      <Container>
        {/* Section Header */}
        <ScrollReveal className="text-center mb-12">
          <span className="text-caption text-apple-blue uppercase tracking-wider font-medium">
            Engagement Model
          </span>
          <h2 className="text-h1 md:text-display-m font-bold mt-3 mb-4">
            Engagement <span className="gradient-text">Packages</span>
          </h2>
          <p className="text-body text-text-secondary max-w-2xl mx-auto">
            Packages are designed for fast decision-making with clear outputs, defined timelines,
            and direct executive access.
          </p>
        </ScrollReveal>

        {/* Pricing Cards */}
        <StaggerContainer className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5 lg:gap-6">
          {engagementTiers.map((tier) => (
            <StaggerItem key={tier.id}>
              <Card
                hover
                glow={tier.highlighted}
                className={`h-full flex flex-col ${
                  tier.highlighted 
                    ? 'border-apple-blue/30 bg-gradient-to-b from-bg-tertiary to-bg-secondary' 
                    : ''
                }`}
              >
                {/* Icon & Name */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      tier.highlighted ? 'bg-apple-blue text-white' : 'bg-bg-primary text-apple-blue'
                    }`}>
                      {tier.icon}
                    </div>
                    <h3 className="text-h4 font-semibold min-w-0">{tier.name}</h3>
                  </div>
                  {tier.highlighted && (
                    <span className="px-3 py-1 bg-apple-blue text-white text-caption font-medium rounded-full whitespace-nowrap">
                      Most Popular
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-body-s text-text-secondary mb-4">
                  {tier.description}
                </p>

                <p className="text-body-s text-text-primary mb-5">
                  {tier.bestFor}
                </p>

                <div className="grid sm:grid-cols-2 gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    tier.highlighted ? 'bg-apple-blue/15' : 'bg-bg-primary'
                  }`}>
                    <CalendarRange className={`w-4 h-4 ${tier.highlighted ? 'text-apple-blue' : 'text-text-secondary'}`} />
                  </div>
                  <div>
                    <p className="text-caption uppercase tracking-wider text-text-tertiary">Timeline</p>
                    <p className="text-body-s text-text-primary">{tier.timeline}</p>
                  </div>
                </div>

                <div className="mb-5 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-1.5 min-w-0">
                    <span className="text-[clamp(2rem,6vw,3rem)] leading-none font-bold text-text-primary break-words min-w-0">
                      {tier.price}
                    </span>
                    {tier.currency && (
                      <span className="text-body-s font-medium text-text-secondary">
                        {tier.currency}
                      </span>
                    )}
                  </div>
                  <span className="text-body-s text-text-tertiary">{tier.unit}</span>
                </div>

                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ClipboardList className="w-4 h-4 text-apple-blue" />
                    <p className="text-caption uppercase tracking-wider text-text-tertiary">
                      Core Deliverables
                    </p>
                  </div>
                  <ul className="space-y-2">
                    {tier.deliverables.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-body-s text-text-secondary">
                        <Check className="w-3.5 h-3.5 mt-0.5 text-apple-blue flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-grow">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        tier.highlighted ? 'bg-apple-blue/20' : 'bg-bg-primary'
                      }`}>
                        <Check className={`w-3 h-3 ${tier.highlighted ? 'text-apple-blue' : 'text-text-secondary'}`} />
                      </div>
                      <span className="text-body-s text-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  variant={tier.highlighted ? 'primary' : 'secondary'}
                  fullWidth
                  href={tier.href}
                >
                  {tier.cta}
                </Button>
              </Card>
            </StaggerItem>
          ))}
        </StaggerContainer>

        {/* Note */}
        <ScrollReveal delay={0.4}>
          <div className="mt-12 text-center">
            <p className="text-body-s text-text-tertiary">
              Minimum engagement applies. Time-sensitive or complex projects are quoted individually.
              <br />
              All engagements are handled under strict confidentiality and Norwegian medical professional standards.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.5}>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-border-subtle bg-bg-tertiary p-4">
              <p className="text-caption uppercase tracking-wider text-apple-blue mb-2">1. Discovery</p>
              <p className="text-body-s text-text-secondary">
                30-minute scoping call to confirm mandate fit, urgency, and stakeholder map.
              </p>
            </div>
            <div className="rounded-xl border border-border-subtle bg-bg-tertiary p-4">
              <p className="text-caption uppercase tracking-wider text-apple-blue mb-2">2. Plan</p>
              <p className="text-body-s text-text-secondary">
                Defined workstream, meeting cadence, decision owners, and expected outputs.
              </p>
            </div>
            <div className="rounded-xl border border-border-subtle bg-bg-tertiary p-4">
              <p className="text-caption uppercase tracking-wider text-apple-blue mb-2">3. Execute</p>
              <p className="text-body-s text-text-secondary">
                Weekly momentum with clear executive updates and risk-controlled delivery.
              </p>
            </div>
          </div>
        </ScrollReveal>
      </Container>
    </Section>
  );
};

export default Engagement;
